import React, { useState, useEffect, useMemo, useCallback } from "react";
import "./LoadBoard.css";
import PropTypes from "prop-types";
import { Outlet } from "react-router-dom";
import { addDays, parseISO } from "date-fns";
import { Box, CircularProgress } from "@mui/material";
import CustomizedTable from "../../CustomizedTable/CustomizedTableLB";
import { DefaultCell, TitleCaseCell, UpCaseCell } from "../../CustomizedTable/CustomCells/CustomCells";
import LoadIdCell from "../../CustomizedTable/CustomCells/OrderIdCell/LoadIdCell";
import CurrencyCell from "../../CustomizedTable/CustomCells/CurrencyCell/CurrencyCell";
import MaxPayCell from "../../CustomizedTable/CustomCells/MaxPayCell/MaxPayCell";
import DateTimeRangeCell from "../../CustomizedTable/CustomCells/DateTimeRangeCell/DateTimeRangeCell";
import WeightCell from "../../CustomizedTable/CustomCells/WeightCell/WeightCell";
import OffersCell from "../../CustomizedTable/CustomCells/OffersCell/OffersCell";
import ActionsCell from "../../CustomizedTable/CustomCells/ActionsCell/ActionsCell";
import BrokerageStatusCell from "../../CustomizedTable/CustomCells/BrokerageStatusCell/BrokerageStatusCell";
import CarrierNameCell from "../../CustomizedTable/CustomCells/CarrierNameCell/CarrierNameCell";
import CarrierRepNameCell from "../../CustomizedTable/CustomCells/CarrierRepNameCell/CarrierRepNameCell";
import ModeWithTempCell from "../../CustomizedTable/CustomCells/ModeWithTempCell/ModeWithTempCell";
import NewRowCell from "../../CustomizedTable/CustomCells/NewRowCell/NewRowCell";
import SelectedCell from "../../CustomizedTable/CustomCells/SelectedCell/SelectedCell";
import MilesCell from "../../CustomizedTable/CustomCells/MilesCell/MilesCell";
import EtaStatusCell from "../../CustomizedTable/CustomCells/EtaStatusCell/EtaStatusCell";
import SelectedFilter from "../../CustomizedTable/Filters/SelectedFilter/SelectedFilter";
import NumberRangeFilter from "../../CustomizedTable/Filters/NumberRangeFilter/NumberRangeFilter";
import DateRangeFilter from "../../CustomizedTable/Filters/DateRangeFilter/DateRangeFilter";
import MultiOptionsFilter from "../../CustomizedTable/Filters/MultipleOptionsFilter/MultipleOptionsFilter";
import BrokerageStatusFilter from "../../CustomizedTable/Filters/BrokerageStatusFilter/BrokerageStatusFilter";
import { sortArrayOfDateObjectsAsc } from "../../../utils/sortHelpers";

export default function LoadBoard({
  isAdminUser,
  tabLabels,
  tabValue,
  isLoading,
  allLoads,
  deleteNewLoads,
  onLoadFieldUpdate,
  customFilters,
  customColumns,
  search,
  setTotalResults,
  handleTableAction
}) {
  const [filteredLoads, setFilteredLoads] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [activeRow, setActiveRow] = useState(null);

  const [tableColumns, setTableColumns] = useState([
    {
      Header: "",
      id: "newlyAdded",
      width: 20,
      Cell: NewRowCell,
    },
    {
      Header: "",
      accessor: "selected",
      Filter: SelectedFilter,
      width: 50,
      Cell: SelectedCell,
      sortDisabled: true,
      showFilter: true
    },
    {
      Header: "Actions",
      id: "actions",
      originPoint: "load-board",
      actions: [isAdminUser && "post-load", "carrier-offer", "shot-clock", "load-booking", "quick-actions"],
      width: isAdminUser ? 178 : 158,
      Cell: ActionsCell,
    },
    {
      Header: "Status",
      accessor: "brokerageStatus",
      Filter: BrokerageStatusFilter,
      filter: "multi",
      width: 100,
      Cell: BrokerageStatusCell,
      disableFilters: true
    },
    {
      Header: "Load",
      accessor: "orderId",
      idKey: "orderId",
      filter: "fuzzyText",
      width: 96,
      Cell: LoadIdCell,
      disableFilters: true
    },
    {
      Header: "Carrier",
      accessor: "carrierName",
      filter: "fuzzyText",
      width: 240,
      idKey: "carrierId",
      Cell: CarrierNameCell,
      disableFilters: true
    },
    {
      Header: "Order",
      accessor: "orderTypeId",
      filter: "fuzzyText",
      width: 100,
      Cell: DefaultCell,
      disableFilters: true
    },
    {
      Header: "BOL #",
      accessor: "blnum",
      filter: "fuzzyText",
      width: 130,
      Cell: DefaultCell,
      disableFilters: true
    },
    {
      Header: "Tender",
      accessor: "tenderType",
      filter: "fuzzyText",
      width: 100,
      Cell: DefaultCell,
      disableFilters: true
    },
    {
      Header: "Customer",
      accessor: "customerName",
      filter: "fuzzyText",
      width: 320,
      Cell: DefaultCell,
      disableFilters: true
    },
    {
      Header: "Mode",
      accessor: "mode",
      filter: "fuzzyText",
      width: 160,
      Cell: ModeWithTempCell,
      disableFilters: true
    },
    {
      Header: "Origin City",
      accessor: "originCity",
      filter: "fuzzyText",
      width: 130,
      Cell: UpCaseCell,
      disableFilters: true
    },
    {
      Header: "OS",
      accessor: "originState",
      Filter: MultiOptionsFilter,
      filter: "multi",
      width: 80,
      Cell: DefaultCell,
      disableFilters: true
    },
    {
      Header: "Pick Up",
      accessor: "originSchedArriveEarly",
      Filter: DateRangeFilter,
      filter: "dateBetween",
      lateDate: "originSchedArriveLate",
      stopType: "Origin",
      width: 140,
      Cell: DateTimeRangeCell,
      disableFilters: true
    },
    {
      Header: "Dest City",
      accessor: "destinationCity",
      filter: "fuzzyText",
      width: 130,
      Cell: UpCaseCell,
      disableFilters: true
    },
    {
      Header: "DS",
      accessor: "destinationState",
      Filter: MultiOptionsFilter,
      filter: "multi",
      width: 80,
      Cell: DefaultCell,
      disableFilters: true
    },
    {
      Header: "Delivery",
      accessor: "destinationSchedArriveEarly",
      Filter: DateRangeFilter,
      filter: "dateBetween",
      lateDate: "destinationSchedArriveLate",
      stopType: "Destination",
      width: 140,
      Cell: DateTimeRangeCell,
      disableFilters: true
    },
    {
      Header: "Miles",
      accessor: "miles",
      Filter: NumberRangeFilter,
      filter: "between",
      width: 90,
      Cell: MilesCell,
      disableFilters: true
    },
    {
      Header: "Weight",
      accessor: "weight",
      Filter: NumberRangeFilter,
      filter: "between",
      width: 100,
      Cell: WeightCell,
      disableFilters: true
    },
    {
      Header: "Commodity",
      accessor: "commodity",
      filter: "fuzzyText",
      width: 130,
      Cell: TitleCaseCell,
      disableFilters: true
    },
    {
      Header: "Customer Rate",
      accessor: "customerTotalCharges",
      Filter: NumberRangeFilter,
      filter: "between",
      width: 150,
      Cell: CurrencyCell,
      disableFilters: true
    },
    {
      Header: "Carrier Pay",
      accessor: "carrierPay",
      Filter: NumberRangeFilter,
      filter: "between",
      width: 150,
      Cell: CurrencyCell,
      disableFilters: true
    },
    {
      Header: "Gross Profit",
      accessor: "grossProfit",
      Filter: NumberRangeFilter,
      filter: "between",
      width: 150,
      Cell: CurrencyCell,
      disableFilters: true
    },
    {
      Header: "Max Pay",
      accessor: "maxPay",
      Filter: NumberRangeFilter,
      filter: "between",
      width: 160,
      Cell: MaxPayCell,
      disableFilters: true
    },
    {
      Header: "Offers",
      accessor: "offerCount",
      Filter: NumberRangeFilter,
      filter: "between",
      width: 120,
      Cell: OffersCell,
      disableFilters: true
    },
    {
      Header: "Carrier Rep",
      accessor: "distpatcherUserId",
      filter: "fuzzyText",
      originPoint: "load-board",
      width: 130,
      Cell: CarrierRepNameCell,
      disableFilters: true
    },
    {
      Header: "Acct Mgr",
      accessor: "accountMgr",
      filter: "fuzzyText",
      width: 120,
      Cell: DefaultCell,
      disableFilters: true
    },
    {
      Header: "Rate Con",
      accessor: "rateConfirmationStatus",
      filter: "fuzzyText",
      width: 110,
      Cell: DefaultCell,
      disableFilters: true
    },
    {
      Header: "Rate Con Sent",
      Filter: DateRangeFilter,
      accessor: "rateConfirmationSentDate",
      filter: "dateBetween",
      width: 140,
      Cell: DateTimeRangeCell,
      disableFilters: true
    },
    {
      Header: "ETA to Next Stop",
      accessor: "newEta",
      Filter: DateRangeFilter,
      filter: "dateBetween",
      width: 160,
      Cell: DateTimeRangeCell,
      disableFilters: true
    },
    {
      Header: "ETA Status",
      accessor: "etaStatus",
      filter: "multiNumeric",
      width: 130,
      tabValue: tabValue,
      Cell: EtaStatusCell,
      disableFilters: true
    },
    {
      Header: "Next Stop",
      accessor: "nextStop",
      filter: "fuzzytext",
      width: 140,
      Cell: DefaultCell,
      disableFilters: true
    },
    {
      Header: "Last Callin Date",
      accessor: "lastCallDateTime",
      Filter: DateRangeFilter,
      filter: "dateBetween",
      width: 150,
      Cell: DateTimeRangeCell,
      disableFilters: true
    },
    {
      Header: "Current location",
      accessor: "currentLocation",
      filter: "fuzzyText",
      width: 150,
      Cell: DefaultCell,
      disableFilters: true
    },
    {
      Header: "Tracking Status",
      accessor: "trackStatus",
      filter: "fuzzyText",
      width: 150,
      Cell: DefaultCell,
      disableFilters: true
    },
    {
      Header: "Next Scheduled Check Call",
      accessor: "nextSchedCall",
      Filter: DateRangeFilter,
      filter: "dateBetween",
      width: 180,
      Cell: DateTimeRangeCell,
      disableFilters: true
    },
    {
      Header: "Distance To Next Stop",
      accessor: "distanceNextStop",
      Filter: NumberRangeFilter,
      filter: "between",
      width: 170,
      Cell: MilesCell,
      disableFilters: true
    },
  ]);

  const loads = useMemo(() => filteredLoads, [filteredLoads]);
  const columns = useMemo(() => tableColumns, [tableColumns, hiddenColumns]);

  const updateData = useCallback((rowIndex, columnID, value) => {
    let updatedOrderId = null;

    setFilteredLoads(old => {
      const updatedRows = old.map((row, index) => {
        if (index === rowIndex) {
          updatedOrderId = row.orderId;
          return {
            ...row,
            [columnID]: value,
          };
        }

        if (columnID === "activeRow" && row.activeRow) {
          return {
            ...row,
            activeRow: false,
          };
        }
        return row;
      });

      setActiveRow(updatedRows.find(({ activeRow }) => activeRow));

      return updatedRows;
    });

    if (updatedOrderId) {
      deleteNewLoads(updatedOrderId);

      if (columnID === "maxPay") {
        onLoadFieldUpdate?.(updatedOrderId, columnID, value);
      }
    }
  }, [deleteNewLoads, onLoadFieldUpdate, setActiveRow]);

  const handleSelectAll = useCallback(value => {
    let updatedLoads = [...filteredLoads];
    updatedLoads.forEach(loads => { loads.selected = value; });
    setFilteredLoads(updatedLoads);
  }, []);

  useEffect(() => {
    if (customColumns.length > 0) {
      let updatedTableColumns = [...tableColumns];
      updatedTableColumns = updatedTableColumns.slice(0, 3);
      customColumns.forEach(cusCol => {
        const col = tableColumns.find(({ accessor }) => accessor === cusCol.columnName);
        updatedTableColumns.push(col);
      });

      setTableColumns(updatedTableColumns);
      setHiddenColumns(customColumns.filter(({ isChecked }) => !isChecked).map(({ columnName }) => columnName));
    }
  }, [customColumns]);

  useEffect(() => {
    const getData = setTimeout(() => {
      let loads = [...allLoads];

      if (customFilters.length > 0) {
        const daysBackFilter = customFilters.find(filter => filter.fieldName === "daysBack");
        const daysOutFilter = customFilters.find(filter => filter.fieldName === "daysOut");
        const startDay = daysBackFilter ? addDays(new Date(), 0 - daysBackFilter.params) : new Date();
        const endDay = daysOutFilter ? addDays(new Date(), daysOutFilter.params) : new Date();
        if (startDay && endDay) {
          loads = loads.filter(({ originSchedArriveEarly }) => {
            const date = parseISO(originSchedArriveEarly);
            return date >= startDay && date <= endDay;
          });
        }

        const filterByField = (fieldName, fieldValue) => {
          let filter = customFilters.find(filter => filter.fieldName === fieldName);
          if (filter) {
            loads = loads.filter(load =>
              load[fieldValue] !== null &&
              load[fieldValue] !== undefined &&
              load[fieldName] !== ""
            );

            filter = filter.params !== "" ? filter.params.split("||") : [];
            if (filter.length > 0) {
              loads = loads.filter(load => filter.includes(load[fieldValue].toString()));
            }
          }
        };

        filterByField("originState", "originState");
        filterByField("originZoneName", "originZoneName");
        filterByField("destinationState", "destinationState");
        filterByField("destinationZoneName", "destinationZoneName");
        filterByField("equipmentTypeId", "mode");
        filterByField("mode", "equipmentTypeId");
        filterByField("brokerageStatus", "brokerageStatus");
        filterByField("customerName", "customerName");
        filterByField("customerRep", "customerRep");
        filterByField("accountMgr", "accountMgr");
        filterByField("operationsRep", "operationsRep");
        filterByField("distpatcherUserId", "distpatcherUserId");
        filterByField("etaStatus", "etaStatus");
      }

      if (search.length > 3) {
        const searchRegex = new RegExp(search, "i");
        loads = loads.filter(load => Object.values(load).some(v => searchRegex.test(v)));
      }

      if (activeRow) {
        const activeOrderId = activeRow?.orderId;
        loads.forEach(load => {
          load.activeRow = load.orderId === activeOrderId;
        });
      }

      if (tabValue === 1) {
        loads = sortArrayOfDateObjectsAsc(loads.filter(load => load.exceptions !== null || load.etaStatus === 3 || load.etaStatus === 2), "currentStopSchedArriveEarly");
        loads = [
          ...loads.filter(load => load.etaStatus === 3 && load.exceptions?.missingArrivalTime),
          ...loads.filter(load => load.etaStatus === 3 && load.exceptions?.missingDepartureTime),
          ...loads.filter(load => load.etaStatus === 3 && !load.exceptions?.missingArrivalTime && !load.exceptions?.missingDepartureTime),

          ...loads.filter(load => load.etaStatus === 2 && load.exceptions?.missingArrivalTime),
          ...loads.filter(load => load.etaStatus === 2 && load.exceptions?.missingDepartureTime),
          ...loads.filter(load => load.etaStatus === 2 && !load.exceptions?.missingArrivalTime && !load.exceptions?.missingDepartureTime),

          ...loads.filter(load => load.etaStatus === 1 && load.exceptions?.missingArrivalTime),
          ...loads.filter(load => load.etaStatus === 1 && load.exceptions?.missingDepartureTime),
          ...loads.filter(load => load.etaStatus === 1 && !load.exceptions?.missingArrivalTime && !load.exceptions?.missingDepartureTime),

          ...loads.filter(load => load.etaStatus < 1 && load.exceptions?.missingArrivalTime),
          ...loads.filter(load => load.etaStatus < 1 && load.exceptions?.missingDepartureTime),
          ...loads.filter(load => load.etaStatus < 1 && !load.exceptions?.missingArrivalTime && !load.exceptions?.missingDepartureTime),
        ];
      }

      setFilteredLoads(loads);
      setTotalResults(loads.length);

      const tab = tabLabels[tabValue];
      const scroll = JSON.parse(localStorage.getItem(`loadboard-tab-${tab?.customLoadBoardByUserID}`)) ?? { x: 0, y: 0 };
      document.getElementById("table-container")?.firstElementChild.scrollTo(scroll.x, 0);
      document.getElementsByClassName("table-list")[0]?.scrollTo(0, scroll.y);

    }, 300);
    return () => clearTimeout(getData);
  }, [allLoads, customFilters, search]);

  useEffect(() => {
    setTableColumns(old => old.map(col => {
      if (col.accessor === "etaStatus") {
        return { ...col, tabValue: tabValue };
      }
      return col;
    }));
    setFilteredLoads([]);
    const tab = tabLabels[tabValue];
    const tabName = tab?.customLoadBoardByUserID;
    setSelectedTab(tabName);

    const getData = () => {
      const divX = document.getElementById("table-container").firstElementChild;
      const divY = document.getElementsByClassName("table-list")[0];
      localStorage.setItem(`loadboard-tab-${tabName}`, JSON.stringify({ x: divX?.scrollLeft, y: divY?.scrollTop }));
    };

    window.addEventListener("scrollend", getData, true);
    return () => window.removeEventListener("scrollend", getData, true);
  }, [tabValue]);

  return (
    <>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", paddingTop: "20px" }}>
          <CircularProgress sx={{ color: "primary.60" }} size={40} />
        </Box>
      ) : (
        <CustomizedTable
          id="load-board"
          columns={columns}
          data={loads}
          updateData={updateData}
          enableRowSelect
          setSelectAll={handleSelectAll}
          hiddenColumns={hiddenColumns}
          actionCallback={handleTableAction}
          selectedTab={selectedTab}
        />
      )}
      <Outlet />
    </>
  );
}

LoadBoard.propTypes = {
  isAdminUser: PropTypes.bool.isRequired,
  tabLabels: PropTypes.array,
  tabValue: PropTypes.number,
  isLoading: PropTypes.bool.isRequired,
  allLoads: PropTypes.array.isRequired,
  deleteNewLoads: PropTypes.func.isRequired,
  onLoadFieldUpdate: PropTypes.func,
  customFilters: PropTypes.array.isRequired,
  customColumns: PropTypes.array.isRequired,
  search: PropTypes.string,
  setTotalResults: PropTypes.func.isRequired,
  handleTableAction: PropTypes.func
};